// The PLAY panel of the hub (R229).
//
// The panel shows two things. The four mode tiles come first, and the
// seats the account already holds stand under them, one card each (R234).
// A player who is in the middle of a season presses RESUME, and a player
// who is not presses a mode. The modes lead, because the panel is named
// PLAY and starting a game is what a player came here to do; a seat card
// is the detail of a game already begun.
//
// What it reads:
//
//   `/api/me`        the shell reads it and hands it over as `ctx.me()`.
//                    It carries the placements (R223), the daily arcade
//                    run counter (R221) and the account's clan (R232).
//   `/api/modes`     the mode schedule (R220), through the cache key
//                    `modes`. The shell reads the same key, so the panel
//                    and the shell together make one request.
//   `/api/clan/clanwar`  the clan's registration for the run (R2826),
//                    through the CLAN panel's cell `CLANWAR_CELL`, only
//                    while the CLAN WAR tile of a member offers MUSTER.
//   `/api/custom/host`  the host state of the custom lobby (§16.6), through
//                    the cell `custom-host`: the acts of the EVENT tile,
//                    the open EVENT tile of a started custom run (R3178)
//                    and the one custom seat card (R3168).
//
// A window's own label stands in the tile's kicker and in no other line.
// The refusal under a closed window is the short form the owner chose for
// every scheduled mode: "STARTS IN 2D 06H" (R232).
//
// The live PLAY is the loudest mark of the panel (R1734). A refusal reads
// in Dim, and the words that fix it ("SEE THE SHOP", "FIND ONE") are a
// text button of their own after the reason. A fix never leads to a closed
// shop: where `/api/shop/plans` answers `shop_disabled`, the tile draws the
// reason alone. The CONQUEST tile of an account with no core seat states
// what PLAY commits to: one season of `SEASON_DAYS` days.
//
// Every countdown here is arithmetic on an instant the server sent. The
// panel subtracts `ctx.now()` and never `Date.now()`, because a telephone
// that has been asleep carries a clock that has drifted (R220).
//
// What is honestly empty today:
//
//   - An account with no placement gets an empty state and no figures. The
//     panel never draws a seat that no world described.
//   - A placement whose world has not beaten for a minute, or whose newest
//     beat belongs to an earlier season, has no `seat` (R223). The card
//     then says the seat is not described and prints no rank, no sov and
//     no queue.
//   - A mode the server did not list is not drawn at all. The panel adds
//     no tile of its own.
//   - A mode whose worlds are all full is refused to a player who holds no
//     seat in it, and offered as RESUME to a player who does (R235). The
//     free-seat refusal is about opening a new seat, and a resume opens
//     none. The other three refusals stand for a resume as well, because
//     the server applies them to every join.
//   - The arcade tile says RESUME only while a match runs (R391). The
//     arcade placement is permanent, so the tile reads the seat the
//     coordinator described and not the placement.
//   - A guest that the next core roll deletes reads the guest card over the
//     tiles from seaquake stage 1 on (R607, R1565, `ui/guestwarn.ts`). Only
//     a guest asks `/api/worlds` for the season end the card counts to.
//   - The arcade draws no seat card at all (R391). A card states what a
//     season seat is worth, and a match has no standing, no sov and no
//     queue. An account whose only placement is the arcade reads the
//     no-seat empty state.
//   - A seat card stands only while its seat can be joined now (R1561):
//     the tile of its mode would offer RESUME. An event seat between two
//     runs draws no card, and an account whose every seat is shut reads an
//     empty state that says so, not the no-seat one.
//
// A seat card answers "is my empire safe, and when must I act next"
// (R1743, ui-map-interface.md §16.6). A siege against the faction stands
// at its head in the Bad ink, and NEXT WINDOW and SEASON ENDS state the
// next two moments to act, from the seat watch of the beat and the season
// end of the world row. Under the cards, one quiet line offers the siege
// alerts while this browser can take web push and the siege class is off.

import { sellsNothing } from '../../../platform/platform';
import { button } from '../../choice/choice';
import {
  getClanWar, getModes, getWorldList, KEY_PREMIUM, type ClanWar, type ModeInfo, type ModeList, type Placement,
  type WorldList,
} from '../../../net/api';
import { getHostState, type HostState } from '../../../net/customlobby';
import { CODE_EVENT_TOO_CLOSE, CUSTOM_LOBBY, HOST_CODE_LEN } from '../../../gen/balance';
import { codeKind, normalizeHostCode } from '../../../arcade/hostcode';
import { CustomLobby, lobbyOfSection, runClockLine, runJoinable, type LobbyDeps } from './customlobby';
import {
  MODE_ARCADE, MODE_CLANWAR, MODE_CORE, MODE_CUSTOM, MODE_EVENT, SEASON_DAYS,
} from '../../../gen/balance';
import { HUB_SEAT_WARN_SEC, KEYS } from '../../../gen/presentation';
import { matches } from '../../../keys';
import { Reels, reelHtml, type ReelName } from '../../reels';
import { seatRowHtml } from '../../briefing';
import { ARCADE_HREF, arcadeHostHref, hubSettingsHref } from '../../hublink';
import { browserEnv, PushAlerts, type PushEnv } from '../../../push/subscribe';
import { assignWithGesture } from '../../nav';
import { closeHowPlays, howModeOf, howPressHtml, openHowPlays, type HowMode } from '../../howplays';
import { guestWarn as G, hubCustom as H, hubPlay as S, hubState as S_STATE } from '../../../gen/strings';
import { guestFacts, nextCoreRoll, warnOnHub } from '../../guestwarn';
import {
  arcadeState, CLANWAR_CELL, clanHoldsNoPlace, findMode, formatSpan, groupDigits, hasKey, heldKeys, inClan,
  maintenanceLine, modeView, seatAlert, seatJoinable, seatView, tierOf, TTL_LIVE, TTL_ON_OPEN,
  type ModeFix, type SeatAlert,
} from '../hubstate';
import {
  card, empty, esc, failed, h1, lede, page, ctlRow, rows, tierLine, wireOnce,
  type PageKey, type Panel, type PanelContext, type Row, type SectionAt, type TabKey,
} from '../panel';
import { shopIsDisabled, shopPlansCell } from './shop';
import { seaViewHref } from '../../../fast/sealink';

/** One tile, with every string already decided. */
interface TileDraw {
  /** The mode name. It is also the tile's colour class in `hub.css`. */
  mode: string;
  name: string;
  kick: string;
  corner: string;
  /** The line a joinable tile carries. */
  state: string;
  /** What the line of a held seat states in place of YOUR SEAT (R1752):
   *  a siege, which takes the Bad ink, a window, or '' for the seat line. */
  alert: SeatAlert['kind'] | '';
  /** Why the tile is locked. An empty string means it is not locked. */
  reason: string;
  /** The section the fix words of the reason line press through to, or ''
   *  for a reason with no fix. `arcade` opens the arcade page (R1741). */
  fix: TileFix;
  cta: string;
  /** Why the CTA of an open tile takes no press (R2826), or '' for a CTA
   *  that takes one. The tile keeps its line, its reel and its button. */
  dead: string;
  /** The world the CTA joins, or 0 to let matchmaking pick one. */
  world: number;
  /** The mode reel the tile plays (R1535). */
  reel: ReelName;
  /** The custom acts under the foot of the EVENT tile (HOST and JOIN BY
   *  CODE, §16.6), as markup, or '' for none. */
  acts?: string;
  /** A press that stands in the foot in place of the CTA, as markup, or ''
   *  for none: the lone press of a live lobby on an EVENT tile that draws
   *  no press of its own (R3205). */
  foot?: string;
}

/** The fixed half of each tile: the mode reel, the name and the kicker.
 *  Each mode plays its own reel (ui-map-interface.md §16.5). */
const SHAPES: Record<string, { name: string; kick: string; cta: string; reel: ReelName }> = {
  core: {
    name: S.mode_core_name, kick: S.mode_core_kick, cta: S.mode_core_cta, reel: 'conquest',
  },
  arcade: {
    name: S.mode_arcade_name, kick: S.mode_arcade_kick, cta: S.mode_arcade_cta, reel: 'arcade',
  },
  event: {
    name: S.mode_event_name, kick: S.mode_event_kick, cta: S.mode_event_cta, reel: 'event',
  },
  clanwar: {
    name: S.mode_clanwar_name, kick: S.mode_clanwar_kick, cta: S.mode_clanwar_cta, reel: 'clanwar',
  },
};

/** The mode word of a seat card heading. A mode the table does not name
 *  reads as the core mode. The placement names its mode, so the table is
 *  a `Map` (R1341). */
const SEAT_MODES: ReadonlyMap<string, string> = new Map([
  [MODE_EVENT, S.mode_event_name],
  [MODE_CLANWAR, S.mode_clanwar_name],
  [MODE_CUSTOM, S.mode_custom_name],
]);

/** The page of the HOW IT PLAYS card that the `?` of a tile opens
 *  (R2445). Every mode tile carries one (R3178). */
const HOW_PAGE: Readonly<Record<string, HowMode>> = {
  core: 'conquest', arcade: 'arcade', event: 'event', clanwar: 'clanwar',
};

/** The cell of `GET /api/custom/host`: may this account host a custom
 *  world now (§16.6). Only a Premium account reads it. */
const HOST_CELL = 'custom-host';

/**
 * The life of the host-state cell, in ms (R3168). It is `TTL_LIVE`, but a
 * started lobby whose open time passed while its world is not open yet
 * reads it every `CUSTOM_LOBBY.pollSec`, so the EVENT tile and the custom
 * seat card flip within seconds of the open. The shell renders the panel
 * once a second, and that render starts the read: the panel runs no timer
 * of its own, and no read runs faster while no started lobby waits.
 */
export function hostCellTtl(hs: HostState | undefined, now: number): number {
  if (hs?.lobbyState !== 'started' || hs.worldOpen === true) return TTL_LIVE;
  return (hs.opensAt ?? 0) > now ? TTL_LIVE : CUSTOM_LOBBY.pollSec * 1000;
}

/** The press of RESUME on a started custom lobby (R3168): the join of its
 *  world once the server opened it, else the lobby page, where the clock
 *  counts to the open. */
function customPress(hs: HostState): { act: string; data: Record<string, string> } {
  return runJoinable(hs.worldId, hs.worldOpen)
    ? { act: 'join', data: { mode: MODE_CUSTOM, world: String(hs.worldId) } }
    : { act: 'ct-return', data: { code: hs.lobby ?? '' } };
}

/** The order the four tiles stand in. It is the panel's own reading
 *  order and not the ticket's numbering, which the generated names carry
 *  (R339). */
const ORDER = [MODE_CORE, MODE_ARCADE, MODE_EVENT, MODE_CLANWAR];

/** The order of the tiles for one schedule. The core and the arcade keep
 *  the first two places. The two scheduled modes take the last two, and a
 *  mode whose window is open stands before a mode whose window is shut
 *  (R1462). Two open or two shut modes keep the ORDER. `live` names a mode
 *  that counts as open with a shut window: the EVENT tile of a started
 *  custom run (R3178). */
function tileOrder(modes: ModeInfo[], live: string[] = []): string[] {
  const open = (mode: string) => live.includes(mode) || findMode(modes, mode)?.open === true;
  const head = ORDER.slice(0, 2);
  const tail = ORDER.slice(2);
  return [...head, ...tail.filter(open), ...tail.filter((m) => !open(m))];
}

/** The fix of a tile: the fix `modeView` names, or the arcade, which the
 *  no-free-seat refusal offers as its next step (R1741). */
type TileFix = ModeFix | 'arcade';

/** The words of each fix, which the tile writes after the reason. One key
 *  per fix keeps every reason a whole sentence in each language. */
const FIX_WORDS: Record<Exclude<TileFix, ''>, string> = {
  shop: S.fix_shop,
  clan: S.fix_clan,
  arcade: S.fix_arcade,
};

/**
 * The presses of the panel that open another section in place, with the
 * page or the section each one opens:
 *
 *   - `alerts`: the siege-alert line opens SETTINGS on ALERTS (R1743);
 *   - `shop`: the fix words of a tile whose key the account does not hold
 *     (R232);
 *   - `clan`: the clanwar tile is locked to a player with no clan, so its
 *     fix words mean FIND A CLAN of CLAN (§16.16);
 *   - `settings`: REGISTER of the guest card opens the ACCOUNT page of
 *     SETTINGS, which links a sign-in method, and the guest keeps its
 *     captains (R1565).
 *
 * The press reads its own `data-act`, so the table is a `Map` (R1341).
 */
const GO_PRESSES: ReadonlyMap<string, readonly [TabKey, (PageKey | SectionAt)?]> = new Map<
  string, readonly [TabKey, (PageKey | SectionAt)?]
>([
  ['alerts', ['settings', 'alerts']],
  ['shop', ['shop']],
  ['clan', ['clan', { section: 'find' }]],
  ['settings', ['settings', 'account']],
]);

/** The label of the window that holds now, or of the next one. */
function windowLabel(m: ModeInfo): string {
  return m.current?.label ?? m.next?.label ?? '';
}

/** One tile, as markup. A locked tile loses its CTA and keeps its reason.
 *  The reason is quiet text; its fix words are the one press of the line
 *  (`hub.css` `.why .fix`).
 *
 *  The line and the CTA share the tile foot, a grid of a text column and
 *  a button column (R1741). The text column yields to the button at every
 *  width and in every language, so a long German line wraps and never runs
 *  under WEITERSPIELEN.
 *
 *  The mode name is the `h2` of the tile, and the CTA names the mode to a
 *  screen reader, "PLAY CONQUEST", from the two words the tile shows
 *  (R2048).
 *
 *  A dead CTA (R2826) stands on an open tile with the `disabled`
 *  attribute and no act, and its name ends with why it takes no press. */
function tileHtml(t: TileDraw): string {
  const locked = t.reason !== '';
  const fix = t.fix === ''
    ? ''
    : ` · <button type="button" class="fix" data-act="${esc(t.fix)}">`
      + `${esc(FIX_WORDS[t.fix])}</button>`;
  const line = locked
    ? `<div class="why">✕ ${esc(t.reason)}${fix}</div>`
    : t.alert === 'siege'
      ? `<div class="st siege">⚠ ${esc(t.state)}</div>`
      : `<div class="st">▸ ${esc(t.state)}</div>`;
  const go = t.foot !== undefined && t.foot !== ''
    ? t.foot
    : locked || t.cta === ''
    ? ''
    : t.dead !== ''
      ? `<button type="button" class="go" disabled aria-label="${esc(`${t.cta} ${t.name} · ${t.dead}`)}">`
        + `${esc(t.cta)}</button>`
      : `<button type="button" class="go" data-act="join" data-mode="${esc(t.mode)}"`
        + `${t.world > 0 ? ` data-world="${t.world}"` : ''} aria-label="${esc(`${t.cta} ${t.name}`)}">`
        + `${esc(t.cta)}</button>`;
  const how = HOW_PAGE[t.mode];
  const acts = t.acts ?? '';
  return `<div class="tile ${esc(t.mode)} dark-screen${locked ? ' locked' : ''}${acts === '' ? '' : ' hosts'}">`
    + reelHtml(t.reel)
    + '<div class="veil"></div>'
    + (how === undefined ? '' : howPressHtml('how', how))
    + `<div class="corner">${esc(t.corner)}</div>`
    + `<div class="body"><div class="kick">${esc(t.kick)}</div>`
    + `<h2 class="nm">${esc(t.name)}</h2><div class="tf">${line}${go}</div>${acts}</div></div>`;
}

/**
 * True when the PLAY panel asks for the siege alerts (R1743): this browser
 * takes web push, the permission is not refused, and the siege class is
 * off. A browser with no push, a server with no push, the iOS step and a
 * load in flight all hide the line, because SETTINGS could not turn the
 * class on there.
 */
function wantsSiegeAlerts(ctl: Pick<PushAlerts, 'status' | 'denied' | 'prefs'>): boolean {
  return ctl.status === 'ready' && !ctl.denied && !ctl.prefs.classes.includes('siege');
}

/** The quiet line of the siege alerts (R1743). The fix words are a link
 *  to SETTINGS · ALERTS; inside the hub the press opens the section in
 *  place (`act`), and the address serves a new tab. */
function pushAskHtml(): string {
  return `<div class="pushask">${esc(S.push_ask)} · `
    + `<a class="fix" href="${esc(hubSettingsHref('alerts'))}" data-act="alerts" role="button">`
    + `${esc(S.push_ask_fix)}</a></div>`;
}

/**
 * The PLAY panel. `pushEnv` is the browser the alert line asks; a test
 * hands a stub.
 */
export function createPlayPanel(ctx: PanelContext, pushEnv?: PushEnv, lobbyDeps?: LobbyDeps): Panel {
  /** The custom lobby page (§16.17). It stands in place of the tiles while
   *  it is open; BACK shows the tiles again, and the lobby stays. */
  const lobby = new CustomLobby(ctx, () => {
    ctx.data.expire(HOST_CELL);
    ctx.refresh();
  }, lobbyDeps);
  /** The text of the code box of the EVENT tile, and whether the last JOIN
   *  met a text that is no code. */
  let typed = '';
  let codeBad = false;

  /** The host-state cell, read with its life of `hostCellTtl`. */
  function hostState(): HostState | undefined {
    const held = ctx.data.peek<HostState>(HOST_CELL).value;
    return ctx.data.cell<HostState>(HOST_CELL, hostCellTtl(held, ctx.now()), getHostState).value;
  }

  /** JOIN BY CODE: a code with a letter first is an arcade game, and a
   *  code with a digit first a custom lobby (R3087). An account in a live
   *  lobby joins no second one (R3168): the tile shows no box then, and
   *  the press does nothing. */
  function joinCode(): void {
    if (ctx.data.peek<HostState>(HOST_CELL).value?.lobby) return;
    const code = normalizeHostCode(typed);
    const kind = codeKind(code);
    codeBad = kind === null;
    if (kind === null) {
      ctx.play('sfx-menu-error');
      ctx.refresh();
      return;
    }
    ctx.play('sfx-menu-press');
    typed = '';
    if (kind === 'arcade') {
      assignWithGesture(arcadeHostHref(code));
      return;
    }
    lobby.open(code);
  }

  /**
   * The custom acts of the EVENT tile (§16.6): HOST, or RETURN TO LOBBY
   * while the account holds a live lobby, and the code box with JOIN.
   * HOST is Premium only: another plan reads why, with the shop as its
   * fix. A Premium account reads `GET /api/custom/host`, and a refusal
   * there greys HOST with the server's sentence. While the read is in
   * flight, or after it failed, HOST stands live and the server's refusal
   * answers the press.
   *
   * A scheduled run that opens too soon shuts hosting for every account
   * (R3150). The tile then drops HOST and the code box and shows no line
   * in their place (R3153). Every account reads the cell for this
   * verdict.
   *
   * An account in a live lobby, as host or as member and with any plan,
   * reads one press and no HOST, code box or JOIN (R3168): RESUME of a
   * started lobby, in the place and the look of the RESUME of a held
   * seat, or RETURN TO LOBBY of a draft or an open lobby.
   */
  function customActs(shopShut: () => boolean): string {
    const me = ctx.me();
    const hs = hostState();
    if (hs?.lobby) return `<div class="cacts lone">${liveLobbyAct(hs)}</div>`;
    if (hs?.reason?.code === CODE_EVENT_TOO_CLOSE) return '';
    let host = '';
    let why = '';
    let fix: TileFix = '';
    if (!hasKey(me, KEY_PREMIUM)) {
      why = H.host_premium;
      fix = shopShut() ? '' : 'shop';
    } else {
      const can = hs === undefined || hs.canHost;
      if (!can) why = hs?.reason?.message ?? '';
      host = `<button type="button" class="go" data-act="ct-host" aria-label="${esc(H.host_name)}"`
        + `${can ? '' : ' disabled'}>${esc(H.host)}</button>`;
    }
    if (lobby.hostNotice !== '') why = lobby.hostNotice;
    const fixHtml = fix === '' ? ''
      : ` · <button type="button" class="fix" data-act="${fix}">${esc(FIX_WORDS[fix])}</button>`;
    const box = `<input type="text" class="ccode" data-in="ct-code" aria-label="${esc(H.code_label)}"`
      + ` maxlength="${HOST_CODE_LEN}" autocapitalize="characters" autocomplete="off" spellcheck="false"`
      + ` placeholder="${esc(H.code_ph)}" value="${esc(typed)}">`;
    const join = `<button type="button" class="go" data-act="ct-join" aria-label="${esc(H.join_name)}">`
      + `${esc(H.join)}</button>`;
    return `<div class="cacts">${host}<span class="cjoin">${box}${join}</span></div>`
      + (why === '' ? '' : `<div class="why cwhy">✕ ${esc(why)}${fixHtml}</div>`)
      + (codeBad ? `<div class="why cwhy">✕ ${esc(H.code_bad)}</div>` : '');
  }

  /** The one press of the EVENT tile while the account sits in a live
   *  lobby (R3168), as a bare button. It stands in the foot when the tile
   *  draws no press of its own, else at the right end of the act row
   *  (R3205). */
  function liveLobbyAct(hs: HostState): string {
    if (hs.lobbyState === 'started') {
      const press = customPress(hs);
      const data = Object.entries(press.data).map(([k, v]) => ` data-${k}="${esc(v)}"`).join('');
      return `<button type="button" class="go" data-act="${press.act}"${data}`
        + ` aria-label="${esc(`${S.cta_resume} ${S.mode_custom_name}`)}">${esc(S.cta_resume)}</button>`;
    }
    return `<button type="button" class="go" data-act="ct-return"`
      + ` data-code="${esc(hs.lobby ?? '')}">${esc(H.host_return)}</button>`;
  }

  /** The reel videos of the tiles. The shell rewrites the pane whenever the
   *  markup changes, and the set puts the videos that already play back in
   *  place of the new ones. */
  const reels = new Reels();
  /** The push state of this browser, read once per visit of the panel and
   *  only while a seat card stands (R1743). */
  let push: PushAlerts | undefined;
  let pushRead = false;
  const pushCtl = (): PushAlerts => (push ??= new PushAlerts(pushEnv ?? browserEnv(), () => ctx.refresh()));

  /** The heading of a seat card: YOUR <MODE> SEAT. */
  const seatTitle = (mode: string): string => S.card_your_seat({ mode: SEAT_MODES.get(mode) ?? S.mode_core_name });

  /** One seat card. Its foot is RESUME THIS SEAT, and OPEN SEA VIEW beside
   *  it (R2828, R3111). RESUME stays the primary press of the card.
   *
   *  `run` is the host state of a started custom lobby, for the custom
   *  card (R3168): until the server opens the world, RESUME opens the
   *  lobby page, OPEN SEA VIEW stands not, and the clock line of the run
   *  heads the card. */
  function seatCard(p: Placement, run?: HostState): string {
    const v = seatView(p, ctx.now());
    const data = { mode: p.mode, world: String(p.worldId) };
    const shut = run !== undefined && !runJoinable(run.worldId, run.worldOpen);
    const sea = shut ? '' : button(S.open_sea_view, { act: 'seaview', data });
    const press = run !== undefined && shut ? customPress(run) : { act: 'join', data };
    const resume = ctlRow(button(S.resume_seat, press, { variant: 'primary' }) + sea);
    const clock = run !== undefined && shut ? lede(runClockLine(run, ctx.now())) : '';
    const end: Row[] = v.seasonEnd === '' ? [] : [[S.row_season_end, v.seasonEnd]];
    if (!v.present) {
      return card(seatTitle(p.mode), '▸', v.asOf,
        clock
        + rows([[S.row_world, v.world], [S.row_season, v.season], ...end])
        + empty(S.seat_undescribed, S.seat_undescribed_why)
        + resume);
    }
    // The siege against the faction heads the card (R1743): it is the one
    // fact that says the empire is not safe.
    const siege = v.siege === null ? '' : `<div class="threat">${seatRowHtml(v.siege)}</div>`;
    // NEXT WINDOW names the object first, on one line (R1752). The site
    // part takes the ellipsis on a narrow card; the time and the span
    // never shrink.
    const win = v.window === '' ? ''
      : `<div class="row wrow"><span class="k">${esc(S.row_window)}</span>`
        + `<span class="v${v.windowOpen ? ' gold' : ''}"><span class="ws">${esc(v.windowSite)}</span>`
        + `<span class="wt">${esc(S_STATE.window_join)}${esc(v.window)}</span></span></div>`;
    const list: Row[] = [
      ...end,
      [S.row_standing, v.rank, 'acc'],
      [S.row_territory, v.territory],
      [S.row_queue, v.queue, v.queueTone],
      [S.row_away, v.away, v.raided ? 'bad' : ''],
    ];
    // The return briefing (R1605, card 05): its rows stand under WHILE YOU
    // WERE OUT, and they go when the commander resumes the seat.
    const brief = v.brief.length === 0
      ? '' : `<div class="aw">${v.brief.map(seatRowHtml).join('')}</div>`;
    const head = rows([[S.row_world, v.world], [S.row_season, v.season]]);
    return card(seatTitle(p.mode), '▸', v.asOf, clock + siege + head + win + rows(list) + brief + resume, `seat-${p.mode}`);
  }

  /**
   * The one custom seat card (R3168, §16.6). It stands while the host-state
   * cell names a started lobby, for the host and for every member, and
   * never from a placement alone. A custom placement in the world of the
   * run draws the regular card body; before the seating writes one, a
   * short card names the world, the clock line of the run and RESUME THIS
   * SEAT, with the press rule of the RESUME on the EVENT tile.
   */
  function customSeatCard(hs: HostState): string {
    const world = hs.worldId ?? 0;
    const p = world > 0
      ? ctx.me().placements.find((x) => x.mode === MODE_CUSTOM && x.worldId === world) : undefined;
    if (p !== undefined) return seatCard(p, hs);
    const press = customPress(hs);
    const sea = runJoinable(hs.worldId, hs.worldOpen)
      ? button(S.open_sea_view, { act: 'seaview', data: { mode: MODE_CUSTOM, world: String(world) } }) : '';
    const name = hs.label !== undefined && hs.label !== '' ? hs.label : H.title;
    return card(seatTitle(MODE_CUSTOM), '▸', '',
      rows([[S.row_world, name]]) + lede(runClockLine(hs, ctx.now()))
      + ctlRow(button(S.resume_seat, press, { variant: 'primary' }) + sea), `seat-${MODE_CUSTOM}`);
  }

  /** The siege-alert line under the seat cards, or '' (R1743). The first
   *  draw with a seat card starts the read; the line stands once it says
   *  the class is off. */
  function pushAsk(): string {
    const ctl = pushCtl();
    if (!pushRead) {
      pushRead = true;
      void ctl.load();
    }
    return wantsSiegeAlerts(ctl) ? pushAskHtml() : '';
  }

  /**
   * The guest warning (R1565): the roll deletes a guest nobody registered
   * (R607), and this card states it from seaquake stage 1 on. The season
   * end comes off the core world list, and the stage off the designed
   * share (`ui/guestwarn.ts`). A registered account asks for no list.
   */
  function guestCard(): string {
    const me = ctx.me();
    if (me.account.kind !== 'guest') return '';
    const cell = ctx.data.cell<WorldList>('worlds-core', TTL_LIVE, () => getWorldList(MODE_CORE));
    const list = cell.value;
    if (list === undefined) return '';
    const now = ctx.now();
    const roll = nextCoreRoll(list.worlds, now);
    if (roll === 0 || !warnOnHub(guestFacts(me), roll, now)) return '';
    return card(G.head, '⚠', '', lede(G.hub_lede({ left: formatSpan(roll - now) }))
      + ctlRow(button(G.register, { act: 'settings' }, { variant: 'primary' })));
  }

  /** The four tiles, from the schedule the server listed. */
  function tiles(): string {
    const cell = ctx.data.cell<ModeList>('modes', TTL_LIVE, () => getModes());
    if (cell.state === 'failed') return failed(cell.error);
    if (cell.state === 'loading' || !cell.value) {
      const waiting = ORDER.map((mode) => tileHtml({
        ...SHAPES[mode]!, mode, corner: '—', state: '—', alert: '', reason: '', fix: '', cta: '', dead: '', world: 0,
      })).join('');
      return `<div class="tiles">${waiting}</div>`;
    }

    const me = ctx.me();
    const now = ctx.now();
    const keys = heldKeys(me);
    // The clanwar mode demands a clan and no key (R232). The tile reads it
    // from `/api/me`, so a locked tile says why without a second request.
    const member = inClan(me);
    /**
     * The seat this account already holds in a mode, if it holds one. A
     * held seat is what turns a tile into a way back rather than a way in
     * (R235).
     *
     * **The arcade reads the described seat and not the placement** (R391).
     * The arcade endpoint is ONE long-lived meta world row, so an account
     * holds an arcade placement for ever after its first arcade join
     * (`join.go`), and the placement alone would make the tile say RESUME
     * for ever. The coordinator names the people who sit in a live match on
     * every heartbeat, so `placement.seat` is present for the arcade only
     * while a match runs, and that is what the tile reads. Every other mode
     * keeps the placement rule: a core seat is a season long, and it stands
     * while the world is silent.
     */
    const heldSeat = (mode: string) => {
      const p = me.placements.find((x) => x.mode === mode);
      if (p === undefined) return undefined;
      if (p.mode === MODE_ARCADE && !seatView(p, now).present) return undefined;
      return p;
    };
    /**
     * True when the shop is closed on this deployment (R1734). The panel
     * reads the SHOP panel's own cell, so the two make one request, and it
     * reads it only when a tile offers the shop as its fix. While the cell
     * loads, or after another failure, the fix stands.
     */
    let shut: boolean | undefined;
    // A store app sells nothing (R2927): its shop is shut too.
    const shopShut = (): boolean => (shut ??= sellsNothing() || shopIsDisabled(shopPlansCell(ctx)));
    /**
     * True when the ARCADE tile offers PLAY now (R1741): its mode is open,
     * it has a world, and the daily runs are not spent. The arcade needs no
     * free seat of a season world, so it is the next step of a tile whose
     * worlds are all full.
     */
    const arcadeRow = findMode(cell.value.modes, MODE_ARCADE);
    const arcadeOpen = arcadeRow !== undefined
      && modeView(arcadeRow, now, keys, member, heldSeat(MODE_ARCADE) !== undefined).joinable
      && arcadeState(me.arcade, now).kind !== 'spent';
    const drawn: string[] = [];
    // A started custom run takes the EVENT tile (R3178): the account holds
    // a seat in a custom world, and the tile is the way back to it.
    const hs = hostState();
    const run = hs?.lobbyState === 'started' ? hs : undefined;

    for (const mode of tileOrder(cell.value.modes, run === undefined ? [] : [MODE_EVENT])) {
      const m = findMode(cell.value.modes, mode);
      // A mode the server did not list is not a mode the hub draws.
      if (!m) continue;
      const s = SHAPES[mode]!;
      const seat = heldSeat(mode);
      const view = modeView(m, now, keys, member, seat !== undefined);
      const label = windowLabel(m);
      let kick = s.kick;
      // A window label is the schedule's own text and may come in mixed
      // case. The kicker is a capitals line with wide tracking, so it shows
      // the label in capitals, as every other kicker stands. The capitals
      // are locale-free, so two machines print one word (R3250).
      if ((mode === MODE_EVENT || mode === MODE_CLANWAR) && label !== '') kick = label.toUpperCase();

      let corner = view.corner;
      let state = view.state;
      let reason = view.reason;
      let fix: TileFix = view.fix;
      // A gated mode whose key the account holds says so in the corner,
      // in the words of the WARDROOM tier tag (R1741, R2046): INCLUDED IN
      // the account's own plan. The bare tier read as a lock to the
      // holder, while the reason under it was a full world.
      const needs = m.entitlement ?? '';
      if (needs !== '' && keys.has(needs)) corner = tierLine(m.tier ?? needs, true, me);
      // Every world of the mode is full: the tile offers the arcade, which
      // needs no free seat, as the next step (R1741).
      if (mode !== MODE_ARCADE && reason === S_STATE.reason_no_free_seat && arcadeOpen) {
        fix = 'arcade';
      }
      let cta = s.cta;
      let dead = '';
      let world = 0;
      let alert: TileDraw['alert'] = '';

      if (mode === MODE_ARCADE) {
        // The daily run counter (R221) is the arcade's own gate, and it
        // stands beside the schedule rather than in place of it. The
        // corner counts the runs only while the mode is open: a closed
        // tile keeps the corner of its schedule, so UNLIMITED or 3/3 never
        // stands over THIS MODE IS NOT OPEN YET (R2175). A spent counter
        // is the reason itself and keeps its corner.
        const arc = arcadeState(me.arcade, now);
        if (reason === '') corner = arc.corner;
        state = arc.line;
        if (reason === '' && arc.kind === 'spent') {
          // The tier the shop really sells: this refusal is the one that
          // does point at a purchase. A closed shop sells no tier, so the
          // line then states the spent runs alone (R1734).
          const upsell = tierOf(me) === 'free' && !shopShut();
          reason = upsell
            ? S.arcade_spent_upsell({ line: arc.line })
            : arc.line;
          fix = upsell ? 'shop' : '';
        }
      }
      // A held seat makes the tile a way back into a game, not a way into
      // a new one (R235). The press names the world, the way the seat
      // card's own button already does, so matchmaking picks nothing and a
      // mode whose worlds are all full still lets its player return. The
      // line says so as well: a RESUME button over "ALWAYS OPEN · 4 100 IN"
      // states the mode and never the seat.
      if (seat) {
        cta = S.cta_resume;
        world = seat.worldId;
        // A mode that is always open drops ALWAYS OPEN from the seat line:
        // the kicker and the corner already say it, and the corner, the
        // kicker and the line said "open" three times (R1741). The line
        // keeps the population. The arcade line is its run counter.
        const alwaysOpen = mode !== MODE_ARCADE && !m.scheduled && m.current?.closesAt === undefined;
        // The watch of the seat (R1752): a siege against its faction, else
        // a window close ahead, says more than YOUR SEAT. The tile is the
        // first thing a seat holder reads on a telephone, and the seat card
        // stands under the tiles.
        const watch = mode === MODE_ARCADE ? null : seatAlert(seat, now, HUB_SEAT_WARN_SEC);
        if (watch !== null) {
          state = watch.text;
          alert = watch.kind;
        } else if (alwaysOpen) {
          state = m.population > 0
            ? S.state_your_seat_population({ n: groupDigits(m.population) })
            : S.state_your_seat;
        } else {
          state = state === ''
            ? S.state_your_seat : S.state_your_seat_with({ state });
        }
      } else if (mode === MODE_CLANWAR && view.joinable && member) {
        // MUSTER of a member whose clan holds no place in the run (R2826):
        // the join goes to the world of the clan's war banner, and a clan
        // with no banner meets `no_banner`. The tile stays open, because
        // the war is on; the button stays and takes no press, and the
        // line says why. The panel reads the CLAN panel's own cell, so the
        // two make one request. While the cell loads, or after it fails,
        // MUSTER keeps its press and the server's refusal answers it.
        const war = ctx.data.cell<ClanWar>(CLANWAR_CELL, TTL_ON_OPEN, getClanWar).value;
        if (clanHoldsNoPlace(war)) {
          dead = S.clan_not_registered;
          state = state === '' ? S.clan_not_registered : S.state_clan_not_registered({ state });
        }
      } else if (mode === MODE_CORE) {
        // The commitment of a first PLAY (R1734): a seat holds for one
        // season. The line keeps the population the mode row carries, and
        // it drops ALWAYS OPEN, which the kicker and the corner already
        // state, so the line fits one row on a telephone.
        state = m.population > 0
          ? S.state_commitment_with({ n: groupDigits(m.population), days: SEASON_DAYS })
          : S.state_commitment({ days: SEASON_DAYS });
      }
      // The EVENT tile of a started custom run (R3178) is open whatever
      // the event window says. The kicker names the custom world, the
      // corner the mode, and the line the seat with the clock of the run.
      // The tile draws no CTA of its own: RESUME of `liveLobbyAct` stands
      // in the foot (R3205), and it joins the world or opens the lobby page.
      if (mode === MODE_EVENT && run !== undefined) {
        // The built-in title holds its capitals in the source string. The
        // name a host typed shows in locale-free capitals (R3250).
        kick = run.label !== undefined && run.label !== '' ? run.label.toUpperCase() : H.title;
        corner = S.mode_custom_name;
        state = S.state_your_seat_with({ state: runClockLine(run, now) });
        alert = '';
        reason = '';
        fix = '';
        cta = '';
        dead = '';
        world = 0;
      }
      // A fix never leads to a closed shop. The reason stays, and the
      // line loses its press.
      if (fix === 'shop' && shopShut()) fix = '';

      // The EVENT tile carries the custom acts under its foot (§16.6):
      // they stand beside the join of an event window, open or shut.
      let acts = mode === MODE_EVENT ? customActs(shopShut) : '';
      // The lone press of a live lobby stands in the foot, beside the line,
      // when the tile draws no press of its own (R3205). A row of its own
      // under the foot lifted the kicker, the name and the line above the
      // text of the tile beside it.
      let foot = '';
      if (mode === MODE_EVENT && hs?.lobby && (reason !== '' || cta === '')) {
        foot = liveLobbyAct(hs);
        acts = '';
      }
      drawn.push(tileHtml({ ...s, mode, kick, corner, state, alert, reason, fix, cta, dead, world, acts, foot }));
    }
    if (drawn.length === 0) {
      return empty(S.no_modes, S.no_modes_why);
    }
    // The maintenance window ahead stands OVER the tiles (deployment.md
    // §6.1): it is about every mode at once, so it belongs to none of
    // them, and a player reads it before they choose where to play.
    const maint = maintenanceLine(cell.value.maintenance, now);
    const line = maint === '' ? '' : `<div class="maint">⚙ ${esc(maint)}</div>`;
    return `${line}<div class="tiles">${drawn.join('')}</div>`;
  }

  return {
    key: 'play',

    html(): string {
      // The custom lobby stands in place of the tiles while it is open.
      if (lobby.active) return page(lobby.html());
      const me = ctx.me();
      // The arcade never gets a seat card (R391). A card states what a
      // SEASON seat is worth — the standing, the sov held, the build queue
      // and what happened while the player was out — and a match has none
      // of those: it is minutes long and awards nothing that persists
      // (arcade.md §2.4). The arcade placement itself stays on `/api/me`,
      // because it is what counts the daily runs and what the tile above
      // reads; this is a display rule and not an API change.
      //
      // An account whose only placement is the arcade therefore reads the
      // no-seat empty state, which is the true answer: it holds no seat.
      //
      // A custom seat draws from the host-state cell and not from the
      // placement (R3168): the cell names the started lobby, and a stale
      // placement of an ended custom world draws no card.
      const seated = me.placements.filter((p) => p.mode !== MODE_ARCADE && p.mode !== MODE_CUSTOM);
      // A seat card stands only while its seat can be joined now (R1561).
      // An event seat between two runs is still a live placement, and its
      // RESUME would meet the closed window. The tile of the mode already
      // says when the next run starts. The schedule is the same cell the
      // tiles read, so the test costs no second request.
      const modes = ctx.data.cell<ModeList>('modes', TTL_LIVE, () => getModes()).value?.modes;
      const now = ctx.now();
      const keys = heldKeys(me);
      const member = inClan(me);
      const open = seated.filter((p) => seatJoinable(p, modes, now, keys, member));
      const hs = hostState();
      const custom = hs?.lobbyState === 'started' ? customSeatCard(hs) : '';
      const cards = open.map((p) => seatCard(p)).join('') + custom;
      // An account whose seats are all shut holds a seat, so the no-seat
      // empty state would be false. It reads its own pair instead.
      const seats = cards !== ''
        ? `<div class="seats">${cards}</div>${pushAsk()}`
        : seated.length > 0
          ? empty(S.no_open_seat, S.no_open_seat_why)
          : empty(S.no_seat, S.no_seat_why);
      // One page root, and the gap between the blocks is the page's own
      // (R231). The panel adds no margin of its own to space a block.
      // Tiles before seats (R234): `.tiles` grows and caps at two rows, so
      // the seat cards take the room left under it. `.seats` puts two cards
      // side by side where the tiles stand two across (R1561).
      // The guest warning stands over the tiles: it is about the account,
      // and a guest reads it before choosing where to play (R1565).
      return page(h1(S.title, S.title_sub), guestCard(), tiles(), seats);
    },

    open(): void {
      // The alerts may have changed in SETTINGS since the last visit.
      pushRead = false;
      lobby.resume();
    },

    /** `/?lobby=<code>` and a sign-in that carried it come here as the
     *  section word `lobby<code>` (§16.17). Any other word opens the
     *  tiles. */
    openSection(word: string): void {
      const code = lobbyOfSection(word);
      if (code !== '') lobby.open(code);
    },

    act(action: string, el: HTMLElement): void {
      if (action.startsWith('cl-')) {
        lobby.act(action, el);
        return;
      }
      if (action === 'ct-host') {
        ctx.play('sfx-menu-press');
        lobby.host();
        return;
      }
      if (action === 'ct-return') {
        const code = el.dataset.code ?? '';
        if (codeKind(code) !== 'custom') return;
        ctx.play('sfx-menu-press');
        lobby.open(code);
        return;
      }
      if (action === 'ct-join') {
        joinCode();
        return;
      }
      if (action === 'join') {
        // The shell plays the launch cue and closes the hub itself, so the
        // panel adds no cue of its own here.
        const mode = el.dataset.mode ?? 'core';
        const world = Number.parseInt(el.dataset.world ?? '0', 10);
        // Arcade lives on its own page (arcade.md §10.1). The tile keeps
        // every gate it had — a spent counter still draws a reason and no
        // button — and the press navigates rather than joins, because the
        // arcade page runs its own join against `mode: 'arcade'`.
        if (mode === MODE_ARCADE) {
          // With the press's activation attached, so the arcade page may
          // keep the menu music playing without another press (ui/nav.ts).
          assignWithGesture(ARCADE_HREF);
          return;
        }
        ctx.join(world > 0 ? { mode, world } : { mode });
        return;
      }
      // OPEN SEA VIEW of a seat card (R2828): the sea view of the seat's
      // world, at the own home cell. The mode goes into the address, so
      // the ticket the sea view asks for names the mode of the seat
      // (seaview.md §3.3 step 5). The hub stores no socket address, and
      // the sea view asks `/api/join` for its ticket.
      if (action === 'seaview') {
        const world = Number.parseInt(el.dataset.world ?? '0', 10);
        if (world <= 0) return;
        ctx.play('sfx-menu-press');
        assignWithGesture(seaViewHref(location.href, world, null, el.dataset.mode ?? MODE_CORE));
        return;
      }
      // The next step of a full mode (R1741): the arcade lives on its own
      // page, as the arcade tile's own press does.
      if (action === 'arcade') {
        ctx.play('sfx-menu-press');
        assignWithGesture(ARCADE_HREF);
        return;
      }
      // The `?` of a tile opens the HOW IT PLAYS card on its mode (R2445).
      if (action === 'how') {
        ctx.play('sfx-menu-press');
        openHowPlays(howModeOf(el.dataset.mode), { press: () => ctx.play('sfx-menu-press') });
        return;
      }
      // A locked tile's fix words, the alert line and REGISTER are a press
      // where a section fixes the reason (`GO_PRESSES`).
      const to = GO_PRESSES.get(action);
      if (to === undefined) return;
      ctx.play('sfx-menu-press');
      ctx.go(...to);
    },

    wire(root: HTMLElement): void {
      if (lobby.active) {
        lobby.wire(root);
        return;
      }
      // The code box of the EVENT tile keeps its text across a rewrite,
      // and Enter in it is JOIN.
      const box = root.querySelector<HTMLInputElement>('input[data-in="ct-code"]');
      if (box && wireOnce(box)) {
        box.addEventListener('input', () => {
          typed = box.value;
          if (codeBad) {
            codeBad = false;
            ctx.refresh();
          }
        });
        box.addEventListener('keydown', (ev) => {
          if (!matches(KEYS.hub.submit, ev)) return;
          ev.preventDefault();
          typed = box.value;
          joinCode();
        });
      }
      reels.attachAll(root);
      // The press route opens ALERTS in place, so the link itself does not
      // load the page again. A press with a modifier keeps the browser's
      // own meaning: a new tab on the same address.
      const ask = root.querySelector<HTMLElement>('.pushask a');
      if (ask && wireOnce(ask)) {
        ask.addEventListener('click', (e) => {
          if (!e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) e.preventDefault();
        });
      }
    },

    close(): void {
      reels.detachAll();
      closeHowPlays();
      lobby.pause();
    },

    destroy(): void {
      reels.destroy();
      lobby.pause();
    },
  };
}
